import { MEAL_NAME_MAP } from "../config/meal-config";
import { buildExercisePayload, buildFavoriteItemPayload } from "./payloads";

const BASELINE_GRAMS = 100;

const round2 = (n) => Number(n.toFixed(2));
const zeroMacros = { kcal: 0, protein: 0, fat: 0, carbs: 0 };

export const calculateNutrientsByAmount = (baseItem, amount, selectedUnit) => {
    if (!baseItem || amount <= 0) return zeroMacros;

    const baseUnitGrams = baseItem.unit?.grams ?? 1;
    const newUnitGrams = selectedUnit?.grams ?? 1;

    const baseGrams = baseItem.amount * baseUnitGrams;
    const newGrams = amount * newUnitGrams;

    if (baseGrams <= 0) return zeroMacros;

    const scale = newGrams / baseGrams;

    return {
        kcal: round2(baseItem.kcal * scale),
        protein: round2(baseItem.protein * scale),
        fat: round2(baseItem.fat * scale),
        carbs: round2(baseItem.carbs * scale),
    };
};

// DIARY SUM HELPERS

export const calculateTotals = (items = []) => {
    const totals = items.reduce(
        (acc, item) => {
            acc.kcal += Number(item.kcal ?? 0);
            acc.carbs += Number(item.carbs ?? 0);
            acc.fat += Number(item.fat ?? 0);
            acc.protein += Number(item.protein ?? 0);
            return acc;
        },
        { kcal: 0, carbs: 0, fat: 0, protein: 0 },
    );

    const round = (num) => Math.round(num * 2) / 2;

    return {
        totalKcal: Math.round(totals.kcal),
        totalCarbs: round(totals.carbs),
        totalFat: round(totals.fat),
        totalProtein: round(totals.protein),
    };
};

export const calculateFoodItemTotals = (items = []) => {
    const totals = items.reduce(
        (acc, item) => {
            if (item._isPerGramNutrition) {
                const grams = resolveEffectiveGrams(item);

                acc.kcal += (item.kcal ?? 0) * grams;
                acc.carbs += (item.carbs ?? 0) * grams;
                acc.fat += (item.fat ?? 0) * grams;
                acc.protein += (item.protein ?? 0) * grams;
            } else {
                acc.kcal += Number(item.kcal ?? 0);
                acc.carbs += Number(item.carbs ?? 0);
                acc.fat += Number(item.fat ?? 0);
                acc.protein += Number(item.protein ?? 0);
            }

            return acc;
        },
        { kcal: 0, carbs: 0, fat: 0, protein: 0 },
    );

    const round = (num) => Math.round(num * 2) / 2;

    return {
        totalKcal: Math.round(totals.kcal),
        totalCarbs: round(totals.carbs),
        totalFat: round(totals.fat),
        totalProtein: round(totals.protein),
    };
};

// ---||---

export const buildFatDataFromItems = (items = []) => {
    const totalFat = items.reduce((sum, item) => sum + (Number(item.fat) || 0), 0);

    // For custom meals without food item data, show total fat as a single value
    return totalFat > 0
        ? {
            labels: ["Totalt Fett"],
            values: [totalFat],
        }
        : {
            labels: [],
            values: [],
        };
};

export const normalizeNutritionData = (apiResponse, items = []) => {
    if (!apiResponse?.data) return {};

    const data = apiResponse.data;

    // Energy distribution
    const energyKeys = Object.keys(data.energy || {});
    const energyValues = Object.values(data.energy || {});

    // Fat types - with fallback to items if API data is empty or all zeros
    let fatKeys = Object.keys(data.fats || {});
    let fatValues = Object.values(data.fats || {});

    // Fallback: if no fat data from API (all zeros) but we have items, calculate from items
    const hasApiFatData = fatValues.some((v) => Number(v) > 0);
    if (!hasApiFatData && items.length > 0) {
        const itemFatData = buildFatDataFromItems(items);
        fatKeys = itemFatData.labels;
        fatValues = itemFatData.values;
    }

    // Other nutrition
    const otherKeys = Object.keys(data.other_nutrition || {});
    const otherValues = Object.values(data.other_nutrition || {});

    // Calories
    const caloriesLabels = ["Kalorier", "Ideal"];
    const caloriesValues = [
        data.calories?.kcal ? Math.max(data.calories.kcal, 0.1) : 0.1,
        data.calories?.ideal ?? 0,
    ];

    // Vitamins & minerals
    const vitaminsMinerals = data.vitamins_minerals || {};
    const vmLabels = Object.keys(vitaminsMinerals);

    const vmPercentage = vmLabels.map((key) => {
        const item = vitaminsMinerals[key];
        if (!item?.rdi || item.rdi === 0) return 0;
        const value = (item.user / item.rdi) * 100;

        return value;
    });

    return {
        energy: {
            labels: energyKeys,
            values: energyValues,
        },
        fat: {
            labels: fatKeys,
            values: fatValues,
        },
        other: {
            labels: otherKeys,
            values: otherValues,
        },
        calories: {
            labels: caloriesLabels,
            values: caloriesValues,
        },
        minerals_vitamins: {
            labels: vmLabels,
            percentage: vmPercentage,
            values: vmLabels.map((key) => {
                const item = vitaminsMinerals[key];
                return item?.user || 0;
            }),
            units: vmLabels.map((key) => {
                const item = vitaminsMinerals[key];
                return item?.unit || "";
            }),
            rdi: vmLabels.map((key) => {
                const item = vitaminsMinerals[key];
                return item?.rdi || 0;
            }),
            rdi_a_day: vmLabels.map((key) => {
                const item = vitaminsMinerals[key];
                return item?.rdi_a_day || 0;
            }),
            tooltip: vmLabels.map((key) => {
                const item = vitaminsMinerals[key];
                return item?.tooltip || "";
            }),
        },
        desired_energy: {
            labels: ["Fett", "Protein", "Kolhydrater"],
            values: [
                data.desired_energy?.fat ?? 0,
                data.desired_energy?.protein ?? 0,
                data.desired_energy?.carbs ?? 0,
            ],
        },
    };
};

export const getMealAmountAndUnit = (item) => {
    if (!item.amount) return "";

    const amount = Number(item.amount).toLocaleString("sv-SE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const unit = item.units?.find((u) => u.id === item.unit?.id) ?? item.unit;

    const unitName =
        unit?.name
            ? `${unit.name}${unit.text?.length > 0 ? ` (${unit.text})` : ""}`
            : unit?.text ?? "gram";


    return `${amount} ${unitName}`;
};

// --- EDITABLE ENTRY BUILDERS ---

// HELPERS

const buildBase = (item, isCustom) => {
    if (!isCustom) {
        const { kcal, protein, fat, carbs, amount, unit } = item;

        return { kcal, protein, fat, carbs, amount, unit };
    }

    const divisor = Number(item.amount) || 1;

    return {
        kcal: item.kcal / divisor,
        protein: item.protein / divisor,
        fat: item.fat / divisor,
        carbs: item.carbs / divisor,
        amount: 1,
        unit: item.unit,
    };
};

const isGramUnit = (item) => item.unit?.grams === 1 && item.unit?.name === "gram";

export const resolveEffectiveGrams = (item) => {
    const portion = Number(item.unit_size);
    if (Number.isFinite(portion) && portion > 0) return portion;

    const weight = Number(item.amount);
    if (Number.isFinite(weight) && weight > 0) return weight;

    return BASELINE_GRAMS;
};

// MAIN

export const normalizeEditableItem = (item, isNew = false) => {
    const isCustom = !item.food_item_id;
    const base = buildBase(item, isCustom);

    // Hydration path — NEVER seed or mutate amount
    if (!isNew) {
        return {
            ...item,
            base,
            _isNew: false,
        };
    }

    // User-add path
    // For template meals (favorites/recipes), preserve stored amount
    // Only seed with unit_size for new items added from food search
    const isFromTemplate = item._favoriteOrigin || item._recipeOrigin;
    const shouldSeed = !isCustom && isGramUnit(item) && !isFromTemplate;

    if (shouldSeed) {
        const amount = resolveEffectiveGrams(item);
        const scaled = calculateNutrientsByAmount(base, amount, item.unit);

        return {
            ...item,
            base,
            amount,
            ...scaled,
            _isNew: true,
            _addedAt: Date.now() + Math.random(),
        };
    }

    return {
        ...item,
        base,
        _isNew: true,
        _addedAt: Date.now() + Math.random(),
    };
};

// REPLACE OR APPEND MERGE

const buildEditableItems = (entry, incoming = []) => {
    const draftItems = entry?.items ?? [];

    // Normalize existing items (ensure base exists)
    const normalizedDraft = draftItems.map((item) => (item.base ? item : normalizeEditableItem(item, false)));

    if (!incoming.length) return normalizedDraft;

    // Normalize incoming items
    const normalizedIncoming = incoming.map((item) => normalizeEditableItem(item, true));

    // Build lookup for incoming ids
    const incomingById = new Map(normalizedIncoming.map((item) => [item.id, item]));

    // Replace existing where ids match
    const replaced = normalizedDraft.map((item) =>
        incomingById.has(item.id) ? incomingById.get(item.id) : item,
    );

    // Append truly new ids
    const existingIds = new Set(normalizedDraft.map((item) => item.id));

    const appended = normalizedIncoming.filter((item) => !existingIds.has(item.id));

    return [...replaced, ...appended];
};

export const buildEditableMeal = (entry, items = []) => ({
    items: buildEditableItems(entry, items),
    name: entry.name,
    time: entry.time,
    isFavorite: entry.isFavorite,
    isRecipe: entry.isRecipe,
    servings: entry.servings,
});

// --- END EDITABLE ENTRY BUILDERS ---

export const hasNewMealItems = (editableMeal, selectedEntry) => {
    const prevItems = selectedEntry?.items ?? [];
    const nextItems = editableMeal?.items ?? [];

    if (prevItems.length === 0) return true;

    if (prevItems.length !== nextItems.length) return true;

    const prevIds = new Set(prevItems.map((i) => i.id).filter(Boolean));

    return nextItems.some((item) => !item.id || !prevIds.has(item.id));
};

const normalizeStandard = (obj) => ({
    exercise_id: Number(obj?.exercise_id ?? ""),
    exercise_name: (obj?.exercise_name ?? "").trim(),
    exercise_duration: Number(obj?.exercise_duration ?? ""),
    exercise_time: obj?.exercise_time ?? "",
    exercise_comment: (obj?.exercise_comment ?? "").trim(),
});

const normalizeSteps = (obj) => ({
    exercise_steps: Number(obj?.exercise_steps ?? ""),
    exercise_time: obj?.exercise_time ?? "",
    exercise_comment: (obj?.exercise_comment ?? "").trim(),
});

const normalizeCustom = (obj) => ({
    name: (obj?.name ?? "").trim(),
    duration: Number(obj?.duration ?? ""),
    kcal: Number(obj?.kcal ?? ""),
    time: obj?.time ?? "",
    comment: (obj?.comment ?? "").trim(),
});

export const exerciseEditorUtils = {
    // NORMALIZERS
    normalizeStandard,

    normalizeCustom,

    normalizeSteps,

    // PAYLOAD BUILDER (exercise editor)
    buildExercisePayload,

    // PAYLOAD BUILDER (favorite item editor)
    buildFavoriteItemPayload,
};

export const calculateEnergyProgress = ({ calories, deficit, energyConsumption }) => {
    const calorieTarget = energyConsumption - deficit;
    const effectiveTarget = calorieTarget + calories.exercise;
    const leftToEat = Math.round(effectiveTarget - calories.food);

    const percentEaten =
        effectiveTarget <= 0 ? 0 : Math.min(Math.max((calories.food / effectiveTarget) * 100, 0), 100);

    const progressbarColor =
        percentEaten < 50
            ? { path: "#749910", trail: "#95C12140" }
            : percentEaten < 75
                ? { path: "#F28714", trail: "#F2871440" }
                : { path: "#EF61A7", trail: "#EF61A740" };

    return { calorieTarget, leftToEat, percentEaten, progressbarColor };
};

export const mergeMealsPreservingOrder = (prevMeals, nextMeals) => {
    const prevIndexMap = new Map();

    Object.values(prevMeals)
        .flat()
        .forEach((meal, index) => {
            if (meal.meal_id != null) {
                prevIndexMap.set(meal.meal_id, meal._uiIndex ?? index);
            }
        });

    let nextIndex = Math.max(-1, ...prevIndexMap.values()) + 1;

    const result = {};

    Object.entries(nextMeals).forEach(([type, meals]) => {
        result[type] = meals.map((meal) => {
            const uiIndex = prevIndexMap.has(meal.meal_id) ? prevIndexMap.get(meal.meal_id) : nextIndex++;

            return { ...meal, _uiIndex: uiIndex };
        });
    });

    return result;
};

export const buildCustomNutritionResponse = (item) => {
    const protein = item.protein ?? 0;
    const fat = item.fat ?? 0;
    const carbs = item.carbs ?? 0;

    const proteinEnergy = protein * 4;
    const carbEnergy = carbs * 4;
    const fatEnergy = fat * 9;

    const total = proteinEnergy + carbEnergy + fatEnergy;

    const percent = (v) => (total > 0 ? Math.round((v / total) * 100) : 0);

    return {
        status: "success",
        data: {
            name: item.name,
            kcal: item.kcal ?? 0,
            nutrition: {
                Protein: protein,
                Fett: fat,
                Kolhydrater: carbs,
            },
            energy: {
                Protein: percent(proteinEnergy),
                Fett: percent(fatEnergy),
                Kolhydrater: percent(carbEnergy),
            },
            calories: {
                kcal: item.kcal ?? 0,
                ideal: 0,
            },
            vitamins_minerals: {},
            fats: {
                Fett: fat,
            },
            other_nutrition: {},
        },
    };
};

export const normalizeMealItems = (source) => {
    if (!source) return [];

    if (Array.isArray(source.items)) return source.items;

    if (source.items) return [source.items];

    return [source];
};

const normalizeForCompare = (items = []) =>
    [...items]
        .map((item) => ({
            food_item_id: Number(item.food_item_id ?? 0),
            amount: Number(parseFloat(item.amount).toFixed(4)),
            unit: Number(item.unit?.id ?? item.unit ?? 0),
        }))
        .sort((a, b) => {
            if (a.food_item_id !== b.food_item_id) return a.food_item_id - b.food_item_id;

            if (a.unit !== b.unit) return a.unit - b.unit;

            return a.amount - b.amount;
        });

export const itemsStructurallyEqual = (a = [], b = []) => {
    const normalizedA = normalizeForCompare(a);
    const normalizedB = normalizeForCompare(b);

    if (normalizedA.length !== normalizedB.length) return false;

    return normalizedA.every((item, index) => {
        const other = normalizedB[index];

        const sameAmount = Math.abs(item.amount - other.amount) < 0.0001;

        return item.food_item_id === other.food_item_id && item.unit === other.unit && sameAmount;
    });
};

/**
 * Validates standard (1) and steps (2) exercise forms.
 * Custom (3) is validated separately.
 */
export const isExerciseFormIncomplete = (form, exerciseTypes = []) => {
    const type = Number(form.exercise_type);

    if (type === 1) {
        const hasId = !!form.exercise_id;
        const hasValidType = hasId && exerciseTypes.some((t) => t.id === Number(form.exercise_id));

        return !hasValidType || !form.exercise_duration || !form.exercise_time;
    }

    if (type === 2) return !form.exercise_steps || !form.exercise_time;

    return true;
};

export const formatDistance = (distance) => {
    const km = Number(distance);

    if (!km || km <= 0) return null;

    // SMALL DISTANCES → METERS
    if (km < 0.1) {
        const meters = Math.round(km * 1000);
        return `${meters} m`;
    }

    // NORMAL DISTANCES → KM
    return `${km.toFixed(1)} km`;
};

export const formatSteps = (steps) => (typeof steps === "number" ? `${steps} steg` : "- steg");

export const hasChartData = (chart) => {
    const values = chart?.values;

    if (!Array.isArray(values)) return false;

    return values.some((v) => Number(v) > 0);
};

export const getChartPlaceholderText = (type, context) => {
    let subject;

    switch (context) {
        case "meal":
            subject = "denna måltid";
            break;
        case "food":
            subject = "detta livsmedel";
            break;
        case "period":
            subject = "denna period";
            break;
        default:
            subject = "detta livsmedel";
    }

    if (type === "desired_energy") return "Ingen önskad energifördelning tillgänglig.";

    const base = {
        energy: "Ingen energidata",
        fat: "Ingen fettdata",
        calories: "Ingen kaloridata",
        other: "Ingen data för övriga ämnen",
        vitamins: "Ingen data för vitaminer och mineraler",
    };

    return `${base[type]} för ${subject}`;
};

export const resolveNutritionContext = ({ response, pathname }) => {
    if (pathname.includes("naringsintag")) return "period";

    const data = response?.data;

    if (data?.items) return "meal";

    if (data?.name) return "food";

    return "food";
};

export const getMealTypeTitle = (entryType) => {
    if (entryType == null) return "Måltid";

    return MEAL_NAME_MAP[entryType] ?? "Måltid";
};

export const formatDuration = (duration) => {
    const minutes = Number(duration);
    return minutes > 0 ? `${minutes} min` : null;
};

// FORMATS PREFIX FOR EXERCISE LIST ITEMS
export const formatExercisePrefix = ({ isSteps = false, time, duration, distance }) => {
    if (!time) return null;

    if (isSteps) return distance ? `${time} - ${distance}` : `${time} -`;

    if (duration) return `${time} - ${duration}`;

    return `${time} -`;
};
